import React, { useEffect, useMemo, useRef, useState } from "react";

import { isClearedFieldValue } from "components/Core/overrideField";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { NumericFormat } from "react-number-format";

export { isClearedFieldValue };

export function ClearFieldAdornment({ onClear, disabled, label }) {
  return (
    <InputAdornment position="end">
      <IconButton
        size="small"
        edge="end"
        aria-label={label || "clear"}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClear}
        disabled={disabled}
      >
        <CloseOutlined fontSize="small" />
      </IconButton>
    </InputAdornment>
  );
}

export const MASK_NUMERIC = "numeric";
export const MASK_NUMBER_UNFORMATTED = "numeric_unformatted";
export const MASK_PERCENTAGE = "percentage";
export const ERROR_MASK_EDIT_PERCENTAGE_FORMAT = "% must be between 0% and 100%";

const NumericFormatInputComponent = React.forwardRef(function NumericFormatInputComponent(props, ref) {
  const { onChange, allowNegative, allowNull: _allowNull, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      allowNegative={allowNegative}
    />
  );
});

const NumberUnformattedFormatInputComponent = React.forwardRef(
  function NumberUnformattedFormatInputComponent(props, ref) {
    const { onChange, allowNegative, allowNull: _allowNull, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        allowNegative={allowNegative}
      />
    );
  }
);

const PercentageFormatInputComponent = React.forwardRef(function PercentageFormatInputComponent(props, ref) {
  const { onChange, value, allowNull = false, ...other } = props;
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    setDisplayValue(value || value === 0 ? parseFloat((value * 100).toFixed(2)).toString() : "");
  }, [value]);

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      value={displayValue}
      onValueChange={(values) => {
        let decimalValue;

        if (values.floatValue == null) {
          decimalValue = allowNull ? null : 0;
        } else if (values.floatValue === 0) {
          decimalValue = 0;
        } else {
          decimalValue = parseFloat((values.floatValue / 100).toFixed(4));
        }

        onChange({
          target: {
            name: props.name,
            value: decimalValue,
          },
        });
      }}
      isAllowed={(values) => {
        const { floatValue } = values;
        return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
      }}
      decimalScale={2}
      allowNegative={false}
      thousandSeparator={false}
      valueIsNumericString
    />
  );
});

// eslint-disable-next-line react-refresh/only-export-components
export const getInputComponentType = (_maskType) => {
  if (_maskType === MASK_NUMERIC) {
    return NumericFormatInputComponent;
  } else if (_maskType === MASK_NUMBER_UNFORMATTED) {
    return NumberUnformattedFormatInputComponent;
  } else if (_maskType === MASK_PERCENTAGE) {
    return PercentageFormatInputComponent;
  } else {
    return "";
  }
};

export function StatefulControlledTextField({
  defaultValue,
  placeholder,
  isRequired,
  isEditable = true,
  disabled,
  onChange,
  onBlur,
  label,
  id,
  name,
  inputProps,
  InputLabelProps,
  FormHelperTextProps,
  variant,
  maskType,
  fullWidth,
  className,
  size,
  isLoading,
  sx,
  showErrorSpace = true,
  showRequiredImmediately = false,
  endAdornment,
  multiline,
  minRows,
  maxRows,
  rows,
  inputRef: externalInputRef,
  errorText,
  allowNull = false,
  restoreValue,
}) {
  const [value, setValue] = useState(defaultValue);
  const [isEdited, setIsEdited] = useState(false);
  const internalInputRef = useRef(null);

  const { allowNegative, ...restInputProps } = inputProps || {};

  const inputRef = externalInputRef || internalInputRef;

  const handleChangeState = (event, value) => {
    if (!event?.target) return;

    const eventValue = event.target.value;
    value = value ?? eventValue;

    if (value == null && !allowNull) return;

    setValue(value);
  };

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const getIsError = useMemo(() => !!errorText || (isRequired ? !value : false), [isRequired, value, errorText]);

  const getErrorText = useMemo(
    () => errorText || (getIsError && (isEdited || showRequiredImmediately) ? "Required" : showErrorSpace ? " " : ""),
    [getIsError, isEdited, showRequiredImmediately, showErrorSpace, errorText]
  );

  const handleOnKeyUp = (e) => {
    const KEY_CODE_ENTER = 13;
    if (e.keyCode === KEY_CODE_ENTER) {
      handleOnBlur(e);
      e.target.select();
    }
  };

  const handleOnBlur = (e) => {
    const isEmpty = isClearedFieldValue(value);
    const canRestore = !isClearedFieldValue(restoreValue);
    if (isEmpty && canRestore) {
      setValue(restoreValue);
      e.target.value = allowNull ? null : restoreValue;
    } else {
      e.target.value = value;
    }
    setIsEdited(true);
    if (onChange) {
      onChange(e);
    } else {
      onBlur?.(e);
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <>
      {isEditable ? (
        <SkeletonWrapper
          variant="textfield"
          sx={{ marginBottom: showErrorSpace ? "20px" : "0px" }}
          isLoading={isLoading}
        >
          <TextField
            value={value}
            onFocus={(e) => !multiline && e.target.select()}
            onChange={handleChangeState}
            onBlur={handleOnBlur}
            label={label}
            placeholder={placeholder}
            name={name}
            variant={variant}
            InputLabelProps={{ ...InputLabelProps, htmlFor: id || name, shrink: true }}
            fullWidth={fullWidth}
            className={className}
            disabled={disabled}
            sx={sx}
            size={size}
            onKeyUp={handleOnKeyUp}
            error={getIsError}
            helperText={getErrorText}
            FormHelperTextProps={FormHelperTextProps}
            multiline={multiline}
            {...(multiline && { minRows, maxRows, rows })}
            InputProps={{
              endAdornment: endAdornment,
              id: id || name,
              autoComplete: "off",
              inputComponent: multiline ? undefined : getInputComponentType(maskType),
              inputRef: inputRef,
              inputProps: {
                ...restInputProps,
                ...(maskType === MASK_PERCENTAGE ? { allowNull } : {}),
                ...(!multiline && (maskType === MASK_NUMERIC || maskType === MASK_NUMBER_UNFORMATTED)
                  ? { allowNegative: allowNegative ?? true }
                  : {}),
              },
            }}
            autoComplete="off"
          />
        </SkeletonWrapper>
      ) : (
        <SkeletonWrapper isLoading={isLoading}>
          <Typography
            variant="body1"
            sx={{
              borderBottom: "1px solid",
              borderColor: "grey.200",
              p: 0.5,
              ...inputProps?.sx,
            }}
          >
            {value || (value === 0 ? "0" : "")}
          </Typography>
        </SkeletonWrapper>
      )}
    </>
  );
}
